/**
 * A member's own private data in a shared session (TMMAPP-0142 U3 item 18): the set the caller
 * saves whole with `PUT …/private-data`, and the copy for its refusal.
 */
import type { TFunction } from 'i18next';
import type { AssistantPrivateData, AssistantSession } from '@/api/assistant';
import { errorCopy } from '@/api/errorCopy';
import { ErrorCodes, isApiError } from '@/api/errors';

/** The caller's own private data in the session, as the PUT names it. */
export function myPrivateData(session: AssistantSession): {
  accountIds: number[];
  categories: string[];
} {
  const mine = session.privateData.filter((item) => item.mine);
  return {
    accountIds: mine.flatMap((item) =>
      item.kind === 'ACCOUNT' && item.accountId !== null ? [item.accountId] : [],
    ),
    categories: mine.flatMap((item) =>
      item.kind === 'CATEGORY' && item.category !== null ? [item.category] : [],
    ),
  };
}

/** The set without one of the caller's items (a banner's Remove). */
export function withoutItem(
  session: AssistantSession,
  item: AssistantPrivateData,
): { accountIds: number[]; categories: string[] } {
  const current = myPrivateData(session);
  return {
    accountIds: current.accountIds.filter(
      (id) => !(item.kind === 'ACCOUNT' && item.accountId === id),
    ),
    categories: current.categories.filter(
      (key) => !(item.kind === 'CATEGORY' && item.category === key),
    ),
  };
}

/** 422 ASSISTANT_SCOPE_INVALID's catalog copy here, else the generic copy. */
export function privateDataError(t: TFunction, error: unknown): string {
  if (isApiError(error) && error.code === ErrorCodes.ASSISTANT_SCOPE_INVALID) {
    return t('assistant.privateData.invalid');
  }
  return errorCopy(t, error, 'assistant.error.generic');
}
